"use client";

import { getModuleByName } from "@/constants/procedure-modules";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { getCompanyName } from "@/lib/textron-data-processor";
import { apiService } from "@/services/api-service";
import {
  AlertCircle,
  CheckCircle,
  FileCheck,
  FileSignature,
  Loader2,
  Plus,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  FRF_AS,
  FRF_IND_AS,
  getStandardProceduresForWorkspace,
  StandardProcedure,
} from "../../constants/standard-procedures";
import { getProcedureRouteId } from "../../utils/procedure-route-mapping";
import { WORKSPACE_TO_SUBSTANTIVE_PREFIX } from "../../utils/substantive-procedures-mapping";
import {
  getSignoffEntriesFromProcedureArray,
  loadSubstantiveProcedures,
  type ProcedureSignoffEntry,
} from "../../utils/substantive-procedures-utils";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import AddProcedureDialog from "./AddProcedureDialog";
import ProcedureSignoffDialog from "./ProcedureSignoffDialog";

interface GenericSubstantiveProceduresProps {
  workspaceName: string;
  area?: "Planning" | "Execution" | "ConcludingProcedures";
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface ProcedureModule {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  status: "pending" | "running" | "completed" | "error";
  category: "testing" | "analysis" | "compliance";
  type: "standard" | "custom" | "module";
  routeId?: string;
  signoffs?: ProcedureSignoffEntry[];
}

const SIGNOFF_LABELS: Record<ProcedureSignoffEntry["type"], string> = {
  prep_so: "P",
  rev_so: "R",
  conc_so: "CR",
  eqr_so: "EQR",
  partner_so: "EP",
};

function getInitials(name: string): string {
  if (!name) return "";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function ProcedureSignoffTokens({
  signoffs,
}: {
  signoffs?: ProcedureSignoffEntry[];
}) {
  if (!signoffs || signoffs.length === 0) return null;
  const byType = (
    ["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"] as const
  ).map((type) => ({
    type,
    label: SIGNOFF_LABELS[type],
    entries: signoffs.filter((e) => e.type === type),
  }));
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
      {byType.map(
        ({ type, label, entries }) =>
          entries.length > 0 && (
            <div
              key={type}
              className="flex items-center gap-0.5"
              title={entries.map((e) => `${e.name}`).join(", ")}
            >
              <span className="shrink-0 font-medium text-white/50">
                {label}:
              </span>
              <div className="flex items-center gap-0.5">
                {entries.slice(0, 3).map((e, i) => (
                  <span
                    key={i}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/20 bg-white/15 font-medium text-white/90"
                  >
                    {getInitials(e.name)}
                  </span>
                ))}
                {entries.length > 3 && (
                  <span className="text-white/50">+{entries.length - 3}</span>
                )}
              </div>
            </div>
          ),
      )}
    </div>
  );
}

export default function GenericSubstantiveProcedures({
  workspaceName,
  area = "Execution",
  onBack,
  setActiveSection,
}: GenericSubstantiveProceduresProps) {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(
    null,
  );
  const [companyName, setCompanyName] = useState<string>("");
  const [procedures, setProcedures] = useState<ProcedureModule[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [loadedWorkspaceName, setLoadedWorkspaceName] =
    useState<string>(workspaceName);
  const [workspacePrefix, setWorkspacePrefix] = useState<string>("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [leaseIndASQ4Answer, setLeaseIndASQ4Answer] = useState<string>("");
  const [engagementFrf, setEngagementFrf] = useState<string | null>(null);

  // Get route prefix for this workspace
  const routePrefix =
    WORKSPACE_TO_SUBSTANTIVE_PREFIX[loadedWorkspaceName] || "";

  // Get standard procedures for this workspace (memoized to avoid unnecessary re-renders)
  const standardProcedures = useMemo(
    () => getStandardProceduresForWorkspace(loadedWorkspaceName, engagementFrf),
    [loadedWorkspaceName, engagementFrf],
  );

  useEffect(() => {
    const loadFrf = async () => {
      try {
        const frfResult = await (window as any).sharePointAPI?.getFrf?.();
        const frf = frfResult?.success ? String(frfResult.frf || "").trim() : "";
        setEngagementFrf(frf || null);
      } catch {
        setEngagementFrf(null);
      }
    };
    loadFrf();
  }, []);

  // Load user engagement role (same rules as Document/Library signoff)
  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find(
          (member: any) => String(member.id) === String(user.id),
        );
        const role = currentUserMember?.role || null;
        setUserEngagementRole(role);
      } catch (error) {
        console.error(
          "Error loading user engagement role for procedure signoff:",
          error,
        );
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  // Load workspace name and prefix from Sections.json
  useEffect(() => {
    const loadWorkspaceInfo = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (
            window as any
          ).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            // Check all areas (Planning, Execution, ConcludingProcedures) to find the workspace
            const areas = [
              "Planning",
              "Execution",
              "ConcludingProcedures",
            ] as const;
            for (const areaKey of areas) {
              if (result.data[areaKey] && result.data[areaKey][workspaceName]) {
                // Found the workspace - use the name and prefix from Sections.json
                // Only update if values have changed to prevent infinite loops
                setLoadedWorkspaceName((prev) =>
                  prev !== workspaceName ? workspaceName : prev,
                );
                setWorkspacePrefix((prev) => {
                  const newPrefix = result.data[areaKey][workspaceName];
                  return prev !== newPrefix ? newPrefix : prev;
                });
                // Update area if it's different from the prop
                if (areaKey !== area) {
                  console.log(
                    `Found workspace ${workspaceName} in ${areaKey} area, but component is set to ${area}`,
                  );
                }
                return; // Found it, stop searching
              }
            }
            // If not found in any area, log a warning but use the prop as fallback
            console.warn(
              `Workspace ${workspaceName} not found in Sections.json for any area, using prop value`,
            );
            setLoadedWorkspaceName((prev) =>
              prev !== workspaceName ? workspaceName : prev,
            );
          }
        } else {
          // Fallback if API not available
          setLoadedWorkspaceName((prev) =>
            prev !== workspaceName ? workspaceName : prev,
          );
        }
      } catch (error) {
        console.warn(
          `Failed to load workspace info for ${workspaceName}`,
          error,
        );
        setLoadedWorkspaceName((prev) =>
          prev !== workspaceName ? workspaceName : prev,
        );
      }
    };
    loadWorkspaceInfo();
  }, [workspaceName, area]);

  useEffect(() => {
    const loadLeaseTailoringAnswer = async () => {
      if (workspaceName !== "Lease Liability - Ind AS") return;
      try {
        const res = await (window as any)?.sharePointAPI?.downloadClientJson?.(
          "Execution_TailoringQuestions_Responses.json",
        );
        const answer =
          res?.data?.["Lease Liability - Ind AS"]?.["lease-ind-as-4"]
            ?.selectedOption || "";
        setLeaseIndASQ4Answer(String(answer));
      } catch {
        setLeaseIndASQ4Answer("");
      }
    };
    loadLeaseTailoringAnswer();
  }, [workspaceName]);

  // Load procedures from SubstantiveProcedures.json
  // Use the original workspaceName prop to look up in SubstantiveProcedures.json
  // This ensures custom workspaces work the same way as standard workspaces
  useEffect(() => {
    // Don't run if workspaceName is not set
    if (!workspaceName) {
      setLoading(false);
      return;
    }

    const loadProcedures = async () => {
      try {
        setLoading(true);
        console.log(
          `[GenericSubstantiveProcedures] Loading procedures for workspace: ${workspaceName}`,
        );
        const data = await loadSubstantiveProcedures();
        console.log(
          `[GenericSubstantiveProcedures] Loaded SubstantiveProcedures.json, looking for workspace: ${workspaceName}`,
        );
        console.log(
          `[GenericSubstantiveProcedures] Available workspaces:`,
          Object.keys(data),
        );

        // Use workspaceName (from prop) to look up in SubstantiveProcedures.json
        // This ensures the workspace name matches what's stored in the JSON file
        const workspaceProcedures =
          workspaceName === "Lease Liability"
            ? data["Lease Liability"] || {}
            : workspaceName === "Lease Liability - Ind AS"
              ? data["Lease Liability - Ind AS"] || data["Lease Liability"] || {}
              : data[workspaceName] || {};
        console.log(
          `[GenericSubstantiveProcedures] Found ${Object.keys(workspaceProcedures).length} procedures for workspace: ${workspaceName}`,
        );

        // Create mapping from procedure name to standard procedure info
        // Get standard procedures fresh each time to avoid stale closures
        const currentStandardProcedures =
          getStandardProceduresForWorkspace(workspaceName, engagementFrf);
        const procedureNameToStandard: Record<string, StandardProcedure> = {};
        currentStandardProcedures.forEach((proc) => {
          procedureNameToStandard[proc.name] = proc;
        });

        const loadedProcedures: ProcedureModule[] = [];

        // Process each procedure in the workspace
        for (const [procedureName] of Object.entries(workspaceProcedures)) {
          if (
            workspaceName === "Lease Liability - Ind AS" &&
            procedureName.trim() === "Lease Accounting Exception" &&
            leaseIndASQ4Answer.trim().toLowerCase() !== "yes"
          ) {
            continue;
          }
          // Explicitly hide deprecated standard procedure from Other Assets
          if (workspaceName === "Other Assets" && procedureName.trim() === "IPE Testing") {
            continue;
          }

          // Ind AS: completeness checks are under Other Assets; skip legacy JSON under Other Receivables
          if (
            engagementFrf === FRF_IND_AS &&
            workspaceName === "Other Receivables" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check")
          ) {
            continue;
          }
          // AS: completeness checks stay under Other Receivables only
          if (
            engagementFrf === FRF_AS &&
            workspaceName === "Other Assets" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check")
          ) {
            continue;
          }

          // Filter procedures based on area/phase
          // For Litigations & Claims: "Assessment" should only appear in Concluding Procedures
          if (
            workspaceName === "Litigations & Claims" &&
            procedureName.trim() === "Assessment"
          ) {
            if (area !== "ConcludingProcedures") {
              // Skip Assessment procedure if not in Concluding Procedures
              console.log(
                `[GenericSubstantiveProcedures] Skipping "${procedureName}" - only available in Concluding Procedures`,
              );
              continue;
            }
          }

          const rawProcedureValue = workspaceProcedures[procedureName];
          const signoffs = getSignoffEntriesFromProcedureArray(
            Array.isArray(rawProcedureValue) ? rawProcedureValue : [],
          );

          const standardProc = procedureNameToStandard[procedureName];

          if (standardProc) {
            // Standard procedure - use the proper route mapping
            const IconComponent = standardProc.icon;
            let procedureRouteId = getProcedureRouteId(
              workspaceName,
              procedureName,
            );
            console.log(
              `[GenericSubstantiveProcedures] Generated route for "${procedureName}":`,
              {
                workspaceName,
                procedureName,
                initialRouteId: procedureRouteId,
                area,
                standardProcId: standardProc.id,
              },
            );

            // Ensure route is generated for Litigations & Claims Assessment procedure
            if (
              workspaceName === "Litigations & Claims" &&
              procedureName.trim() === "Assessment"
            ) {
              // Always force the Assessment procedure to its dedicated route
              const prefix = "litclm-substantive";
              procedureRouteId = `${prefix}-assessment`;
              console.log(
                `[GenericSubstantiveProcedures] Forced route generation for Litigations & Claims Assessment:`,
                procedureRouteId,
              );
            }

            // Ensure route is generated for Management Use of Expert Assessment procedure
            if (
              workspaceName === "Management Use of Expert" &&
              procedureName.trim() === "Assessment"
            ) {
              procedureRouteId = `mgtexp-assessment`;
              console.log(
                `[GenericSubstantiveProcedures] Forced route generation for Management Use of Expert Assessment:`,
                procedureRouteId,
              );
            }

            // Prefix with "concluding-" if we're in ConcludingProcedures area
            if (area === "ConcludingProcedures" && procedureRouteId) {
              procedureRouteId = `concluding-${procedureRouteId}`;

              // Harden routing for Litigations & Claims Assessment in Concluding area
              if (
                workspaceName === "Litigations & Claims" &&
                procedureName.trim() === "Assessment"
              ) {
                procedureRouteId = "concluding-litclm-substantive-assessment";
              }

              console.log(
                `[GenericSubstantiveProcedures] Prefixed route for Concluding Procedures:`,
                procedureRouteId,
              );
            }
            loadedProcedures.push({
              id: standardProc.id,
              name: procedureName,
              description: `Standard procedure: ${procedureName}`,
              icon: <IconComponent className="h-5 w-5" />,
              status: "pending",
              category: getCategoryForProcedure(standardProc.id),
              type: "standard",
              routeId: procedureRouteId || undefined,
              signoffs,
            });
          } else {
            if (
              workspaceName === "Other Assets" &&
              (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
                procedureName.trim() === "Capital Advances - Completeness Check") &&
              engagementFrf !== FRF_IND_AS
            ) {
              continue;
            }
            if (
              workspaceName === "Other Receivables" &&
              (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
                procedureName.trim() === "Capital Advances - Completeness Check") &&
              engagementFrf === FRF_IND_AS
            ) {
              continue;
            }
            // Check if it's a module
            const module = getModuleByName(procedureName);
            if (module) {
              // Module procedure
              const IconComponent = module.icon;
              loadedProcedures.push({
                id: module.id,
                name: procedureName,
                description: module.description,
                icon: <IconComponent className="h-5 w-5" />,
                status: "pending",
                category: "compliance" as any, // Use compliance category for purple theme
                type: "module",
                routeId: `custom-procedure:${encodeURIComponent(loadedWorkspaceName)}:${encodeURIComponent(procedureName)}`,
                signoffs,
              });
            } else {
              // Custom procedure
              loadedProcedures.push({
                id: `custom-${procedureName.toLowerCase().replace(/\s+/g, "-")}`,
                name: procedureName,
                description: "Custom procedure with fixed template",
                icon: <FileCheck className="h-5 w-5" />,
                status: "pending",
                category: "testing" as any,
                type: "custom",
                routeId: `custom-procedure:${encodeURIComponent(loadedWorkspaceName)}:${encodeURIComponent(procedureName)}`,
                signoffs,
              });
            }
          }
        }

        console.log(
          `[GenericSubstantiveProcedures] Loaded ${loadedProcedures.length} procedures total`,
        );
        setProcedures(loadedProcedures);
      } catch (error) {
        console.error(
          "[GenericSubstantiveProcedures] Failed to load procedures",
          error,
        );
        setProcedures([]); // Set empty array on error so loading stops
      } finally {
        setLoading(false);
      }
    };

    // Only run when workspaceName, area, or loadedWorkspaceName changes
    // Don't depend on standardProcedures or routePrefix as they're derived values
    // that will cause infinite loops if included in dependencies
    loadProcedures();
  }, [workspaceName, area, loadedWorkspaceName, leaseIndASQ4Answer, engagementFrf]);

  // Helper to determine category based on procedure ID
  const getCategoryForProcedure = (
    procedureId: string,
  ): "testing" | "analysis" | "compliance" => {
    if (
      procedureId.includes("ipe") ||
      procedureId.includes("exception") ||
      procedureId.includes("testing")
    ) {
      return "testing";
    }
    if (
      procedureId.includes("analysis") ||
      procedureId.includes("reconciliation") ||
      procedureId.includes("ageing")
    ) {
      return "analysis";
    }
    if (
      procedureId.includes("compliance") ||
      procedureId.includes("remuneration")
    ) {
      return "compliance";
    }
    return "testing";
  };

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  const handleProcedureSelect = (procedure: ProcedureModule) => {
    console.log("[GenericSubstantiveProcedures] Procedure selected:", {
      name: procedure.name,
      routeId: procedure.routeId,
      hasSetActiveSection: !!setActiveSection,
    });
    if (setActiveSection && procedure.routeId) {
      console.log(
        "[GenericSubstantiveProcedures] Navigating to:",
        procedure.routeId,
      );
      setActiveSection(procedure.routeId);
    } else {
      console.warn(
        "[GenericSubstantiveProcedures] Cannot navigate - missing routeId or setActiveSection",
        {
          routeId: procedure.routeId,
          hasSetActiveSection: !!setActiveSection,
        },
      );
    }
  };

  const handleProcedureAdded = async () => {
    // Reload procedures after adding
    setAddDialogOpen(false);
    try {
      setLoading(true);
      const data = await loadSubstantiveProcedures();
      // Use workspaceName (from prop) to look up in SubstantiveProcedures.json
      const workspaceProcedures =
        workspaceName === "Lease Liability"
          ? data["Lease Liability"] || {}
          : workspaceName === "Lease Liability - Ind AS"
            ? data["Lease Liability - Ind AS"] || data["Lease Liability"] || {}
            : data[workspaceName] || {};

      // Get standard procedures fresh each time to avoid stale closures
      const currentStandardProcedures =
        getStandardProceduresForWorkspace(workspaceName, engagementFrf);
      const procedureNameToStandard: Record<string, StandardProcedure> = {};
      currentStandardProcedures.forEach((proc) => {
        procedureNameToStandard[proc.name] = proc;
      });

      const loadedProcedures: ProcedureModule[] = [];

      for (const [procedureName] of Object.entries(workspaceProcedures)) {
        // Explicitly hide deprecated standard procedure from Other Assets
        if (workspaceName === "Other Assets" && procedureName.trim() === "IPE Testing") {
          continue;
        }

        if (
          engagementFrf === FRF_IND_AS &&
          workspaceName === "Other Receivables" &&
          (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
            procedureName.trim() === "Capital Advances - Completeness Check")
        ) {
          continue;
        }
        if (
          engagementFrf === FRF_AS &&
          workspaceName === "Other Assets" &&
          (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
            procedureName.trim() === "Capital Advances - Completeness Check")
        ) {
          continue;
        }

        const standardProc = procedureNameToStandard[procedureName];

        if (standardProc) {
          const IconComponent = standardProc.icon;
          let procedureRouteId = getProcedureRouteId(
            workspaceName,
            procedureName,
          );
          // Prefix with "concluding-" if we're in ConcludingProcedures area
          if (area === "ConcludingProcedures" && procedureRouteId) {
            procedureRouteId = `concluding-${procedureRouteId}`;
          }
          loadedProcedures.push({
            id: standardProc.id,
            name: procedureName,
            description: `Standard procedure: ${procedureName}`,
            icon: <IconComponent className="h-5 w-5" />,
            status: "pending",
            category: getCategoryForProcedure(standardProc.id),
            type: "standard",
            routeId: procedureRouteId || undefined,
          });
        } else {
          if (
            workspaceName === "Other Assets" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check") &&
            engagementFrf !== FRF_IND_AS
          ) {
            continue;
          }
          if (
            workspaceName === "Other Receivables" &&
            (procedureName.trim() === "Advance to Suppliers - Completeness Check" ||
              procedureName.trim() === "Capital Advances - Completeness Check") &&
            engagementFrf === FRF_IND_AS
          ) {
            continue;
          }
          // Check if it's a module
          const module = getModuleByName(procedureName);
          if (module) {
            // Module procedure
            const IconComponent = module.icon;
            loadedProcedures.push({
              id: module.id,
              name: procedureName,
              description: module.description,
              icon: <IconComponent className="h-5 w-5" />,
              status: "pending",
              category: "compliance" as any, // Use compliance category for purple theme
              type: "module",
              routeId: `custom-procedure:${encodeURIComponent(loadedWorkspaceName)}:${encodeURIComponent(procedureName)}`,
            });
          } else {
            // Custom procedure
            loadedProcedures.push({
              id: `custom-${procedureName.toLowerCase().replace(/\s+/g, "-")}`,
              name: procedureName,
              description: "Custom procedure with fixed template",
              icon: <FileCheck className="h-5 w-5" />,
              status: "pending",
              category: "testing" as any,
              type: "custom",
              routeId: `custom-procedure:${encodeURIComponent(loadedWorkspaceName)}:${encodeURIComponent(procedureName)}`,
            });
          }
        }
      }

      setProcedures(loadedProcedures);
    } catch (error) {
      console.error("Failed to reload procedures after adding", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: ProcedureModule["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "running":
        return (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
        );
      default:
        return (
          <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
        );
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "testing":
        return "border-blue-500/20 bg-blue-500/10 text-blue-300";
      case "analysis":
        return "border-green-500/20 bg-green-500/10 text-green-300";
      case "compliance":
        return "border-purple-500/20 bg-purple-500/10 text-purple-300";
      default:
        return "border-gray-500/20 bg-gray-500/10 text-gray-300";
    }
  };

  /** Card-level accent: left stripe + subtle hover; text stays white */
  const getCategoryCardAccent = (category: string) => {
    switch (category) {
      case "testing":
        return {
          card: "border border-blue-500/25 bg-blue-500/5 border-l-4 border-l-blue-500 hover:bg-blue-500/12 hover:border-blue-500/35",
          title: "text-white",
          icon: "text-white",
        };
      case "analysis":
        return {
          card: "border border-green-500/25 bg-green-500/5 border-l-4 border-l-green-500 hover:bg-green-500/12 hover:border-green-500/35",
          title: "text-white",
          icon: "text-white",
        };
      case "compliance":
        return {
          card: "border border-violet-500/25 bg-violet-500/5 border-l-4 border-l-violet-500 hover:bg-violet-500/12 hover:border-violet-500/35",
          title: "text-white",
          icon: "text-white",
        };
      default:
        return {
          card: "border border-gray-500/25 bg-gray-500/5 border-l-4 border-l-gray-500 hover:bg-gray-500/12 hover:border-gray-500/35",
          title: "text-white",
          icon: "text-white",
        };
    }
  };

  const filteredProcedures =
    selectedCategory === "all"
      ? procedures
      : procedures.filter((p) => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-white/60" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {loadedWorkspaceName} - Substantive Procedures
          </h2>
          <p className="text-gray-400">
            Comprehensive analysis and testing procedures for{" "}
            {loadedWorkspaceName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Procedure
          </Button>
          {!isArchivalMode && (
            <Button
              variant="outline"
              onClick={() => setSignoffDialogOpen(true)}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              <FileSignature className="mr-2 h-4 w-4" />
              Sign off
            </Button>
          )}
          {onBack && (
            <Button variant="outline" onClick={onBack}>
              ← Back
            </Button>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-400">Filter by category:</span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={selectedCategory === "all" ? "default" : "outline"}
            onClick={() => setSelectedCategory("all")}
            className={
              selectedCategory === "all" ? "bg-blue-600" : "border-white/10"
            }
          >
            All
          </Button>
          <Button
            size="sm"
            variant={selectedCategory === "testing" ? "default" : "outline"}
            onClick={() => setSelectedCategory("testing")}
            className={
              selectedCategory === "testing" ? "bg-blue-600" : "border-white/10"
            }
          >
            Testing
          </Button>
          <Button
            size="sm"
            variant={selectedCategory === "analysis" ? "default" : "outline"}
            onClick={() => setSelectedCategory("analysis")}
            className={
              selectedCategory === "analysis"
                ? "bg-green-600"
                : "border-white/10"
            }
          >
            Analysis
          </Button>
          <Button
            size="sm"
            variant={selectedCategory === "compliance" ? "default" : "outline"}
            onClick={() => setSelectedCategory("compliance")}
            className={
              selectedCategory === "compliance"
                ? "bg-purple-600"
                : "border-white/10"
            }
          >
            Compliance
          </Button>
        </div>
      </div>

      {/* Procedures Grid */}
      {procedures.length === 0 ? (
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-12 text-center">
            <FileCheck className="mx-auto mb-4 h-12 w-12 text-white/40" />
            <h3 className="mb-2 text-lg font-semibold">No Procedures Added</h3>
            <p className="mb-4 text-white/60">
              Add your first procedure to get started
            </p>
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Procedure
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredProcedures.map((procedure) => {
            const accent = getCategoryCardAccent(procedure.category);
            return (
              <Card
                key={procedure.id}
                className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] ${accent.card} ${
                  !procedure.routeId ? "cursor-not-allowed opacity-50" : ""
                }`}
                onClick={() => {
                  console.log(
                    "[GenericSubstantiveProcedures] Card clicked for procedure:",
                    procedure.name,
                    "routeId:",
                    procedure.routeId,
                  );
                  handleProcedureSelect(procedure);
                }}
              >
                <CardHeader>
                  <CardTitle
                    className={`flex items-center justify-between ${accent.title}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={accent.icon}>{procedure.icon}</span>
                      <span className="text-lg font-semibold">
                        {procedure.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(procedure.status)}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="mb-3 text-sm text-white/80">
                    {procedure.description}
                  </p>
                  <ProcedureSignoffTokens signoffs={procedure.signoffs} />
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className={`rounded px-2 py-1 text-xs font-medium ${getCategoryColor(procedure.category)}`}
                    >
                      {procedure.category.charAt(0).toUpperCase() +
                        procedure.category.slice(1)}
                    </span>
                    <span className="text-xs text-white/60">
                      {procedure.type === "standard"
                        ? "Standard"
                        : procedure.type === "custom"
                          ? "Custom"
                          : "Module"}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Procedure Dialog */}
      <AddProcedureDialog
        isOpen={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        workspaceName={loadedWorkspaceName}
        workspacePrefix={workspacePrefix || ""}
        area={area}
        onProcedureAdded={handleProcedureAdded}
      />

      {/* Sign off Dialog */}
      <ProcedureSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={loadedWorkspaceName}
        procedureNames={procedures.map((p) => p.name)}
        user={user}
        userEngagementRole={userEngagementRole}
        onSignoffDone={handleProcedureAdded}
      />

      {/* Summary Statistics */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Procedure Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">
                {procedures.filter((p) => p.category === "testing").length}
              </div>
              <div className="text-sm text-gray-400">Testing Procedures</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {procedures.filter((p) => p.category === "analysis").length}
              </div>
              <div className="text-sm text-gray-400">Analysis Procedures</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-400">
                {procedures.filter((p) => p.category === "compliance").length}
              </div>
              <div className="text-sm text-gray-400">Compliance Procedures</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {procedures.length}
              </div>
              <div className="text-sm text-gray-400">Total Procedures</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
